/**
 * The phone's bottom bar (Employee B3).
 *
 * 🔴 IT WAS NOT FILTERED AT ALL. Home · Helpdesk · Maintenance · Visitors ·
 * More went to every role, so an employee or a receptionist tapping
 * Maintenance landed on "You don't have access to this page". Every item now
 * passes the same canOfferInNav the sidebar asks, so the bar can never offer a
 * page the route then refuses.
 *
 * The employee experience gets its own five: Home · Book · Requests · Visitors
 * · Menu, where Menu opens the drawer rather than Settings — the drawer is
 * where the rest of their list lives.
 */
import type React from 'react';
import {
  HomeIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  EllipsisHorizontalIcon,
  CalendarDaysIcon,
  Bars3Icon,
  CheckBadgeIcon,
  ShieldCheckIcon,
  QrCodeIcon,
  BellIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { canOfferInNav } from '@/config/routeAccess';
import { EMPLOYEE_HOME, isEmployeeExperience } from '@/config/employeeExperience';
import { FM_HOME, isFmExperience } from '@/config/fmExperience';
import { RECEPTION_BOTTOM_NAV, isReceptionShell } from '@/config/receptionExperience';
import { isTechShell, techBottomNav } from '@/config/techExperience';
import type { UserRole } from '@/types';

export interface BottomNavItem {
  label: string;
  /** A route to open, or null for the item that opens the menu drawer. */
  path: string | null;
  /** A query the item adds to `path` — an item that opens a pop-up ON a
   *  page (the FM's Approvals opens C29 on /fm). It is active only while the
   *  query is there, and the plain item for the same path is not. */
  search?: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const BOTTOM_NAV: BottomNavItem[] = [
  { label: 'Home', path: '/dashboard', icon: HomeIcon },
  { label: 'Helpdesk', path: '/helpdesk', icon: TicketIcon },
  { label: 'Maintenance', path: '/maintenance', icon: WrenchScrewdriverIcon },
  { label: 'Visitors', path: '/visitors', icon: UserGroupIcon },
  { label: 'More', path: '/settings', icon: EllipsisHorizontalIcon },
];

// Employee rebuild C General: the employee's own pages — My Day, Book, My
// requests, My visitors — not the staff modules the first cut pointed at.
const EMPLOYEE_BOTTOM_NAV: BottomNavItem[] = [
  { label: 'Home', path: EMPLOYEE_HOME, icon: HomeIcon },
  { label: 'Book', path: '/book', icon: CalendarDaysIcon },
  { label: 'Requests', path: '/my-requests', icon: TicketIcon },
  { label: 'Visitors', path: '/my-visitors', icon: UserGroupIcon },
  { label: 'Menu', path: null, icon: Bars3Icon },
];

// FM Manager rebuild C0.4 (decision Q7): Home · Tickets · Approvals · Permits
// · Menu. Tickets is the FM tickets screen (W2); Approvals (W8) opens C29 on
// the home (/fm?approvals=1 — the home opens the pop-up and drops the query
// on close). Permits is not on the bar yet.
const FM_BOTTOM_NAV: BottomNavItem[] = [
  { label: 'Home', path: FM_HOME, icon: HomeIcon },
  { label: 'Tickets', path: '/fm/tickets', icon: TicketIcon },
  { label: 'Approvals', path: FM_HOME, search: '?approvals=1', icon: CheckBadgeIcon },
  // C0.4: Permits came with W6 (C14); permit:view decides whether it shows.
  { label: 'Permits', path: '/fm/permits', icon: ShieldCheckIcon },
  { label: 'Menu', path: null, icon: Bars3Icon },
];

// FM Technician rebuild C1 (D1): Home · Jobs · Scan · Rounds · Menu. Each
// item goes to its technician page once that page ships, and to the existing
// page that does the job until then (config/techExperience TECH_BOTTOM_NAV);
// Rounds is R3 (C9); Alerts held its slot in R2 and stays in the menu.
const TECH_ICONS: Record<string, BottomNavItem['icon']> = {
  home: HomeIcon, jobs: WrenchScrewdriverIcon, scan: QrCodeIcon, rounds: ClipboardDocumentListIcon,
  alerts: BellIcon, menu: Bars3Icon,
};

// Reception rebuild C3: Home · Visitors · Scan · Registers · Menu. Scan is the
// check-in page on its camera tab, so the query travels as `search`.
const DESK_ICONS: Record<string, BottomNavItem['icon']> = {
  home: HomeIcon, visitors: UserGroupIcon, scan: QrCodeIcon, registers: ClipboardDocumentListIcon, menu: Bars3Icon,
};

type NavUser = { role: UserRole; permissions?: string[] | null } | null | undefined;

export function bottomNavFor(user: NavUser): BottomNavItem[] {
  if (isTechShell(user)) {
    return techBottomNav(user, (p) => canOfferInNav(user, p))
      .map((i) => ({ label: i.label, path: i.path, icon: TECH_ICONS[i.id] ?? HomeIcon }));
  }
  if (isReceptionShell(user)) {
    return RECEPTION_BOTTOM_NAV.flatMap((i): BottomNavItem[] => {
      const icon = DESK_ICONS[i.id] ?? HomeIcon;
      if (i.path === null) return [{ label: i.label, path: null, icon }];
      const [path = i.path, query] = i.path.split('?');
      if (!canOfferInNav(user, path)) return [];
      return [{ label: i.label, path, ...(query ? { search: `?${query}` } : {}), icon }];
    });
  }
  const items = isFmExperience(user) ? FM_BOTTOM_NAV
    : isEmployeeExperience(user) ? EMPLOYEE_BOTTOM_NAV : BOTTOM_NAV;
  return items.filter((i) => i.path === null || canOfferInNav(user, i.path));
}
